/**
 * What a CSR is allowed to *do* to an estimate, and what each action costs.
 *
 * Headless on purpose: which controls an estimate offers is a business
 * question, not a rendering one, and the answer has to be identical on every
 * shell. The shell decides what a disabled button looks like; this file decides
 * whether it is disabled and what sentence explains it.
 *
 * Two controls, and neither is the boolean it first looks like.
 *
 * **Recording an outcome is not "cancel".** Tharstern already records why an
 * estimate ended, in the free-text `StatusRef` paired with the integer
 * `Ordered` code, and the business has an established vocabulary for it. The
 * decode that turns those two columns into a commercial status lives in
 * `modules_dbt/dbt/models/sales/intermediate/int_est__base.sql`:
 *
 *     case d.ordered
 *         when '0' then 'No'  when '1' then 'Yes'  when '2' then 'Superseded'
 *         when '4' then coalesce(d.status_ref, '')
 *         when '5' then coalesce(d.status_ref, '')
 *         else ''
 *     end
 *
 * So `Ordered` 4 and 5 are **not outcomes** — they mean "the outcome is
 * whatever StatusRef says". The proof they are not a live/dead split is in the
 * live data: `Ordered = 4` carries both "Order Expected" (which that SQL
 * classifies as **won**) and "No longer required / quote only" (**lost**).
 * Grouping the picker by the integer would therefore put a won signal in a
 * cancellation list. The grouping here is by what the classifier actually
 * does with the StatusRef string.
 *
 * What 4 and 5 *do* mean is a second, independent axis, and it is load-bearing:
 * `EstimateStatus` in `modules/tharstern_estimating_module/models/
 * estimate_enums.py` names them CUSTOM_ALLOW_JOB and CUSTOM_DISALLOW_JOB —
 * whether Tharstern will still let someone raise a job from the estimate. So
 * recording an outcome sets two things at once, and the picker says so.
 *
 * The consequence worth staring at: three values the business uses regularly —
 * "Tender", "No Feedback", "On hold with customer" — appear in *none* of the
 * classifier's won/lost/superseded lists, so they fall through to `open` or
 * `stale`. They are **states, not outcomes**. That is why they are not in the
 * outcome picker at all: they get their own control — **Park / chase** — whose
 * whole framing is "the conversation is still going", rather than being mixed
 * into a list of ways to close an estimate. Same write underneath (a CUSTOM
 * status with its StatusRef text), different question being answered.
 *
 * Writes never go to the view module (it is read-only). Each control names the
 * verb it posts to the owning module — both reach a live endpoint — but the
 * posting itself is the shell's job; nothing in this file performs a write.
 */

import { useEffect, useState } from "react";

import type {
  EstimateDetailViewItem,
  EstimateOrigin,
  EstimateStatusInfo,
  SetEstimateStatusBody,
} from "../data/api-types";
import { useDataAdapter } from "../data/DataAdapterContext";

// ── The outcome vocabulary ──────────────────────────────────────────────────

/**
 * What `int_est__base.sql` classifies a StatusRef string as.
 *
 * `still_open` is not a value the SQL emits — it is the name for "this string
 * matches none of the won/lost/superseded lists, so the estimate stays open (or
 * ages into `stale`)". Naming it makes the fall-through visible instead of
 * looking like an oversight.
 */
export type EstimateOutcomeClass = "won" | "lost" | "superseded" | "still_open";

/**
 * The Tharstern status code an outcome writes.
 *
 * From `EstimateStatus` in
 * `modules/tharstern_estimating_module/models/estimate_enums.py`. Only the four
 * an outcome can produce are named here; the enum also carries
 * AWAITING_PROCESSING (3), NO_CREATE_JOB (6) and AWAITING_APPROVAL (7), which
 * are workflow states rather than outcomes.
 *
 * The 4/5 pair is the part that matters and the part that is easy to get wrong.
 * They are **not** live-versus-dead: they are CUSTOM_ALLOW_JOB and
 * CUSTOM_DISALLOW_JOB — two slots for an install-configured status string that
 * differ only in **whether Tharstern will still let someone raise a job from
 * this estimate**. Both carry their text in `CustomStatusDescription`, which is
 * the column the replica reads back as `StatusRef`. The commercial outcome
 * lives entirely in that text; the integer only governs convertibility.
 */
export const ORDERED = 1;
export const SUPERSEDED = 2;
export const CUSTOM_ALLOW_JOB = 4;
export const CUSTOM_DISALLOW_JOB = 5;

export type EstimateTharsternStatusCode =
  | typeof ORDERED
  | typeof SUPERSEDED
  | typeof CUSTOM_ALLOW_JOB
  | typeof CUSTOM_DISALLOW_JOB;

export interface EstimateOutcomeOption {
  /**
   * The exact string written to Tharstern's `CustomStatusDescription`, read
   * back by the replica as `StatusRef`. Never reworded — the classifier matches
   * on the literal text, so "No Feedback" and "Negative feedback received" are
   * different outcomes, and a tidied-up label would silently reclassify the
   * estimate.
   */
  statusRef: string;
  /** What the dbt classifier does with this string. */
  classifiesAs: EstimateOutcomeClass;
  /** The `EstimateStatus` integer this outcome sets. */
  tharsternStatus: EstimateTharsternStatusCode;
  /**
   * Rows carrying this StatusRef in 180 days of live Tharstern, from the
   * static snapshot below — `null` for an option that snapshot did not know
   * about (a live-only string Tharstern offers that our table has never
   * seen). `0` means the classifier recognises the string but nobody has
   * used it in six months — worth offering only if the business wants to
   * start. Kept visible rather than filtered so the picker can say which
   * options are actually in use.
   */
  liveRowCount: number | null;
}

/**
 * Whether Tharstern will still allow a job to be raised from an estimate in
 * this state — the real meaning of the 4/5 split.
 */
export function outcomeAllowsJobCreation(
  option: EstimateOutcomeOption,
): boolean {
  return option.tharsternStatus !== CUSTOM_DISALLOW_JOB;
}

export interface EstimateOutcomeGroup {
  outcomeClass: EstimateOutcomeClass;
  heading: string;
  /** Why these belong together, in the CSR's terms. */
  explanation: string;
  /** False for `still_open`: picking one of these leaves the estimate live. */
  closesTheEstimate: boolean;
  /**
   * Set when this group cannot be picked here at all, with the reason.
   *
   * Nothing carries one today. It stays because the honest-blocked rendering it
   * drives is the right answer for any future group whose precondition we
   * genuinely cannot satisfy in the UI.
   */
  requiresInstead: string | null;
  options: EstimateOutcomeOption[];
}

/**
 * Whether this outcome needs the rep to name the estimate that replaces this
 * one — a hard Tharstern precondition, not a policy choice:
 * `set_estimate_status` raises `StatusPreconditionError` unless a
 * `revision_estimate_id` accompanies status 2.
 *
 * Derived from the status code rather than declared per group, so a new
 * superseded option cannot be added without the input appearing with it.
 */
export function outcomeNeedsReplacementEstimate(
  option: EstimateOutcomeOption,
): boolean {
  return option.tharsternStatus === SUPERSEDED;
}

/**
 * Every outcome the business uses, grouped by what the classifier does with it.
 *
 * Counts are from 180 days of live Tharstern, and the `tharsternStatus` on each
 * option is the code that StatusRef was actually observed paired with — not a
 * guess. The zero-count entries are in the classifier's own match lists but
 * unused in that window: the vocabulary on paper rather than in practice.
 */
const OUTCOME_GROUPS: EstimateOutcomeGroup[] = [
  {
    outcomeClass: "lost",
    heading: "The customer is not going ahead",
    explanation:
      "Closes the estimate and counts it as lost. These are the reasons the " +
      "win/loss reporting is built from, so the one you pick is the one the " +
      "business sees.",
    closesTheEstimate: true,
    requiresInstead: null,
    options: [
      {
        statusRef: "Lost on price",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 740,
      },
      {
        // The odd one out, and it is odd in live data rather than here: this
        // is the only lost reason paired with ALLOW_JOB, so an estimate closed
        // with it can still be converted to a job. Flagged rather than
        // silently normalised — it may be a Tharstern status misconfiguration.
        statusRef: "No longer required / quote only",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_ALLOW_JOB,
        liveRowCount: 209,
      },
      {
        statusRef: "Cannot meet deadline",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 27,
      },
      {
        statusRef: "Lost on price >25%",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 12,
      },
      {
        statusRef: "Missed Quote Deadline",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 10,
      },
      {
        statusRef: "Too expensive",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 0,
      },
      {
        statusRef: "Customer lost job",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 0,
      },
      {
        statusRef: "Negative feedback received",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 0,
      },
      {
        statusRef: "Not ordered",
        classifiesAs: "lost",
        tharsternStatus: CUSTOM_DISALLOW_JOB,
        liveRowCount: 0,
      },
    ],
  },
  {
    outcomeClass: "won",
    heading: "The customer is going ahead",
    explanation:
      "Marks the estimate as won without claiming the order exists in " +
      "Tharstern yet. Plain 'Ordered' is deliberately NOT offered: an " +
      "estimate is ordered when the customer approves, supplies artwork and " +
      "pays — at that point the system records it and raises the job, so it " +
      "is never a status a CSR/AM keys in.",
    closesTheEstimate: true,
    requiresInstead: null,
    options: [
      {
        statusRef: "Order Expected",
        classifiesAs: "won",
        tharsternStatus: CUSTOM_ALLOW_JOB,
        liveRowCount: 127,
      },
      {
        statusRef: "Verbal confirmation",
        classifiesAs: "won",
        tharsternStatus: CUSTOM_ALLOW_JOB,
        liveRowCount: 0,
      },
      {
        statusRef: "Waiting for PO",
        classifiesAs: "won",
        tharsternStatus: CUSTOM_ALLOW_JOB,
        liveRowCount: 0,
      },
      {
        statusRef: "Order due in",
        classifiesAs: "won",
        tharsternStatus: CUSTOM_ALLOW_JOB,
        liveRowCount: 0,
      },
    ],
  },
  {
    outcomeClass: "superseded",
    heading: "Replaced by a different quote",
    explanation:
      "Closes this estimate without calling it won or lost, because the work " +
      "moved to another quote. You name the quote it moved to — Tharstern " +
      "links the two, which it only does by itself when the replacement was " +
      "raised as a revision.",
    closesTheEstimate: true,
    requiresInstead: null,
    options: [
      {
        statusRef: "Superseded",
        classifiesAs: "superseded",
        tharsternStatus: SUPERSEDED,
        liveRowCount: 1664,
      },
      {
        statusRef: "Quantity change",
        classifiesAs: "superseded",
        tharsternStatus: SUPERSEDED,
        liveRowCount: 0,
      },
      {
        statusRef: "Spec changed",
        classifiesAs: "superseded",
        tharsternStatus: SUPERSEDED,
        liveRowCount: 0,
      },
      {
        statusRef: "Price change required",
        classifiesAs: "superseded",
        tharsternStatus: SUPERSEDED,
        liveRowCount: 0,
      },
      {
        statusRef: "Revise quotation",
        classifiesAs: "superseded",
        tharsternStatus: SUPERSEDED,
        liveRowCount: 0,
      },
      {
        statusRef: "Linked to another estimate",
        classifiesAs: "superseded",
        tharsternStatus: SUPERSEDED,
        liveRowCount: 0,
      },
    ],
  },
];

/**
 * The still-live vocabulary, offered by its own control — Park / chase — not
 * by the outcome picker.
 *
 * Deliberately not a member of `OUTCOME_GROUPS`: none of these is an outcome
 * (the classifier matches none of them), and mixing "where the conversation
 * stands" into a list of ways to close an estimate is how a CSR parks a quote
 * believing they cancelled it. Same `EstimateOutcomeGroup` shape and the same
 * setstatus write underneath; the split is in what question the control asks.
 */
const PARK_GROUP: EstimateOutcomeGroup = {
  outcomeClass: "still_open",
  heading: "Still live — parks it, does not close it",
  explanation:
    "None of these count as an outcome. The estimate stays open and will " +
    "still age into 'stale' after 90 days exactly as if nothing had been " +
    "recorded. They say where the conversation is, not how it ended — but " +
    "they do not agree with each other on whether a job can still be raised.",
  closesTheEstimate: false,
  requiresInstead: null,
  options: [
    {
      statusRef: "Tender",
      classifiesAs: "still_open",
      tharsternStatus: CUSTOM_ALLOW_JOB,
      liveRowCount: 395,
    },
    {
      statusRef: "No Feedback",
      classifiesAs: "still_open",
      tharsternStatus: CUSTOM_DISALLOW_JOB,
      liveRowCount: 128,
    },
    {
      statusRef: "On hold with customer",
      classifiesAs: "still_open",
      tharsternStatus: CUSTOM_DISALLOW_JOB,
      liveRowCount: 104,
    },
  ],
};

/** The outcome vocabulary, grouped by what the dbt classifier does with it. */
export function estimateOutcomeGroups(): EstimateOutcomeGroup[] {
  return OUTCOME_GROUPS;
}

/** The still-live vocabulary behind Park / chase. See `PARK_GROUP`. */
export function estimateParkGroup(): EstimateOutcomeGroup {
  return PARK_GROUP;
}

/**
 * One option by its exact StatusRef string, or null. Searches the whole
 * vocabulary — outcomes and park states — because the string is the identity
 * either way.
 */
export function estimateOutcomeOption(
  statusRef: string,
): EstimateOutcomeOption | null {
  for (const group of [...OUTCOME_GROUPS, PARK_GROUP]) {
    const found = group.options.find((o) => o.statusRef === statusRef);
    if (found) return found;
  }
  return null;
}

// ── Merging the static table with Tharstern's live vocabulary ──────────────
//
// `OUTCOME_GROUPS` / `PARK_GROUP` above are a point-in-time snapshot (usage
// counts from 180 days of live Tharstern, captured by hand). Two things
// drift out from under a snapshot: Tharstern's admin can retire a
// StatusRef the table still lists, or add one the table has never seen. The
// live `GET /tharstern/estimating/statuses` response is the source of truth
// for *availability* and *allows_job_creation* — the classifier grouping
// (won/lost/superseded/still_open) stays ours, because that mapping is dbt's
// (`int_est__base.sql`), not Tharstern's, and the statuses endpoint has no
// opinion on it.

function knownStatusRefs(): Set<string> {
  return new Set(
    [...OUTCOME_GROUPS.flatMap((g) => g.options), ...PARK_GROUP.options].map(
      (o) => o.statusRef,
    ),
  );
}

/** Narrow an arbitrary live status code to the four an outcome can set, or
 * `null` for a system code (e.g. AWAITING_APPROVAL) that is not one of
 * them — those never reach the picker either way. */
function asTharsternStatusCode(
  value: number,
): EstimateTharsternStatusCode | null {
  return value === ORDERED ||
    value === SUPERSEDED ||
    value === CUSTOM_ALLOW_JOB ||
    value === CUSTOM_DISALLOW_JOB
    ? value
    : null;
}

/**
 * One static option, live-checked: present only if Tharstern's response
 * still offers this exact StatusRef, with `tharsternStatus` (and so
 * `allows_job_creation`) taken from Tharstern's row rather than the
 * snapshot — Tharstern's admin config can reassign a description between
 * the allow/disallow codes, and the live row is the current truth.
 * `null` when Tharstern no longer offers it: "a string in our table that
 * Tharstern no longer offers must not be offered."
 */
function liveCheckedOption(
  option: EstimateOutcomeOption,
  liveStatuses: EstimateStatusInfo[],
): EstimateOutcomeOption | null {
  const live = liveStatuses.find((s) => s.description === option.statusRef);
  if (!live) return null;
  const tharsternStatus = asTharsternStatusCode(live.status);
  if (tharsternStatus === null) return null;
  return { ...option, tharsternStatus };
}

function liveCheckedGroup(
  group: EstimateOutcomeGroup,
  liveStatuses: EstimateStatusInfo[],
): EstimateOutcomeGroup {
  return {
    ...group,
    options: group.options
      .map((o) => liveCheckedOption(o, liveStatuses))
      .filter((o): o is EstimateOutcomeOption => o !== null),
  };
}

/** The outcome groups, filtered to what Tharstern still offers. */
export function mergedEstimateOutcomeGroups(
  liveStatuses: EstimateStatusInfo[],
): EstimateOutcomeGroup[] {
  return OUTCOME_GROUPS.map((g) => liveCheckedGroup(g, liveStatuses));
}

/**
 * The Park / chase group, filtered to what Tharstern still offers, plus any
 * live StatusRef neither the outcome lists nor the static park list
 * recognise.
 *
 * An unrecognised string is not a special case to hide — it is exactly what
 * the classifier's own fall-through means: a StatusRef matching none of the
 * won/lost/superseded lists leaves the estimate open, ageing to `stale` like
 * anything else unmatched. That *is* Park / chase's question ("where does
 * the conversation stand?"), so a live-only string belongs in this picker,
 * not hidden pending a table update. `liveRowCount: null` — the 180-day
 * snapshot was taken before CIS knew this string existed, so "0 uses" would
 * be a claim we cannot back up, not an honest count.
 */
export function mergedEstimateParkGroup(
  liveStatuses: EstimateStatusInfo[],
): EstimateOutcomeGroup {
  const known = knownStatusRefs();
  const merged = liveCheckedGroup(PARK_GROUP, liveStatuses);
  const unrecognised: EstimateOutcomeOption[] = [];
  for (const live of liveStatuses) {
    if (!live.description || known.has(live.description)) continue;
    const tharsternStatus = asTharsternStatusCode(live.status);
    if (tharsternStatus === null) continue;
    unrecognised.push({
      statusRef: live.description,
      classifiesAs: "still_open",
      tharsternStatus,
      liveRowCount: null,
    });
    // Avoid offering the same live-only string twice if it appears more
    // than once in Tharstern's response.
    known.add(live.description);
  }
  return { ...merged, options: [...merged.options, ...unrecognised] };
}

export type LiveEstimateVocabulary =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      outcomeGroups: EstimateOutcomeGroup[];
      parkGroup: EstimateOutcomeGroup;
    };

/**
 * Fetches Tharstern's live status vocabulary and merges it with the static
 * classifier tables above, for the two pickers (Record outcome, Park / chase).
 *
 * Refetches on every mount rather than once per page view: both pickers'
 * step-1 content lives inside a Radix `Dialog`, which unmounts while closed,
 * so this naturally re-runs each time either control is opened instead of
 * firing a request for every estimate page view whether or not the CSR ever
 * opens either dialog.
 *
 * `"error"` on failure is a dead end by design, not an oversight: falling
 * back to the static snapshot would silently offer a StatusRef Tharstern's
 * admin may have retired, which is exactly the confident-but-wrong this
 * page's other honesty conventions refuse. A vocabulary that failed to load
 * is reported as unavailable, not backfilled.
 */
export function useLiveEstimateVocabulary(): LiveEstimateVocabulary {
  const adapter = useDataAdapter();
  const [state, setState] = useState<LiveEstimateVocabulary>({
    status: "loading",
  });

  useEffect(() => {
    let live = true;
    setState({ status: "loading" });
    adapter
      .getEstimateStatuses()
      .then((statuses) => {
        if (!live) return;
        setState({
          status: "ready",
          outcomeGroups: mergedEstimateOutcomeGroups(statuses),
          parkGroup: mergedEstimateParkGroup(statuses),
        });
      })
      .catch(() => {
        if (live) setState({ status: "error" });
      });
    return () => {
      live = false;
    };
  }, [adapter]);

  return state;
}

/**
 * The status the estimate would report as, once this outcome is recorded.
 *
 * `still_open` maps to "open" because that is literally what the classifier
 * returns — the string matches nothing, so the estimate falls through to the
 * age check. Surfacing it lets the confirm step state the consequence rather
 * than implying every recorded reason closes something.
 */
export function outcomeResultingStatus(
  option: EstimateOutcomeOption,
): "won" | "lost" | "superseded" | "open" {
  return option.classifiesAs === "still_open" ? "open" : option.classifiesAs;
}

// ── Availability ────────────────────────────────────────────────────────────

export interface EstimateControlState {
  available: boolean;
  /** Why not, when unavailable. Always a sentence, never null when blocked. */
  blockedReason: string | null;
  /**
   * A caution that does not block. Used where the control works but the
   * consequence is easy to miss — or where we genuinely do not know yet and
   * would rather say so than present a confident button.
   */
  caution: string | null;
}

export interface EstimateControlsPresentation {
  recordOutcome: EstimateControlState;
  /** Park / chase: records where a live conversation stands. */
  parkChase: EstimateControlState;
}

function originKind(origin: EstimateOrigin): string | null {
  return origin.coverage === "unavailable" ? null : (origin.kind ?? null);
}

// Both controls need only Tharstern's own integer id
// (information.source_estimate_id): the setstatus write addresses the estimate
// by that id alone, not by anything in the raw-replica/dbt-fact detail. So a
// row still waiting on the sync is not blocked — a CIS-authored estimate
// carries this id from the moment Tharstern creates it (see the field's own
// docstring on EstimateInformation), which is why most `pending_sync` rows
// already have one. This only fires in the case the field's docstring calls
// out — `source_estimate_id` failed to parse as an integer — which real data
// does not exercise.
const MISSING_SOURCE_ID_REASON =
  "Tharstern has not returned an id One can address for this estimate, so " +
  "there is no record to write a status to. This is unusual — if it does " +
  "not clear shortly, the sync needs a look.";

const LOCKED_STATUS_WRITE_CAUTION =
  "This estimate is locked in Tharstern. We have not confirmed whether " +
  "Tharstern accepts a status change on a locked estimate — that needs " +
  "checking before this is wired up.";

/**
 * Which controls this estimate offers, and the sentence for each one it does
 * not.
 *
 * The bar is the page's own honesty convention: a thing the system cannot do
 * says why. So a blocked control is rendered disabled with its reason rather
 * than hidden — except where the underlying field does not exist at all for
 * this origin, which is still stated rather than silently dropped, because a
 * CSR who used the control on another estimate yesterday will otherwise
 * assume the page is broken.
 */
export function estimateControlsPresentation(
  estimate: EstimateDetailViewItem,
): EstimateControlsPresentation {
  const { origin, information } = estimate;
  const kind = originKind(origin);
  const revision = information.revision;

  // 1. Nothing was readable. Neither control can be honestly offered, and
  //    "try again" is the only true instruction.
  if (information.coverage === "unavailable" && kind === null) {
    const reason =
      "This estimate could not be read, so there is nothing safe to change. " +
      "It may be outside your portfolio, or the service may be unavailable.";
    return {
      recordOutcome: { available: false, blockedReason: reason, caution: null },
      parkChase: { available: false, blockedReason: reason, caution: null },
    };
  }

  // 2. An earlier revision, or one already replaced. Acting on it would write
  //    an outcome onto a row nothing reports on.
  const isSupersededRow =
    estimate.status === "superseded" || revision?.is_current_revision === false;
  if (isSupersededRow) {
    const reason =
      "This is an earlier version of the quote — it has already been " +
      "replaced. Open the current revision to change anything.";
    return {
      recordOutcome: { available: false, blockedReason: reason, caution: null },
      parkChase: { available: false, blockedReason: reason, caution: null },
    };
  }

  // `== null` deliberately catches `undefined` too: the field is optional
  // *and* nullable on the generated type, so a row from before this field
  // existed (or a fixture that never set it) omits it rather than nulling
  // it — both mean the same thing here, "nothing to address the write to".
  const missingSourceId = information.source_estimate_id == null;

  // Locked and already-won do not block recording an outcome — the status
  // field is not a costing field — but both are easy to miss, so each carries
  // a caution. The locked one is an admitted unknown rather than a warning:
  // whether Tharstern accepts a StatusRef write on a locked estimate has not
  // been verified, and claiming otherwise on a pricing surface is exactly the
  // confident-but-wrong the rest of this page refuses to do.
  const recordOutcome: EstimateControlState = missingSourceId
    ? {
        available: false,
        blockedReason: MISSING_SOURCE_ID_REASON,
        caution: null,
      }
    : {
        available: true,
        blockedReason: null,
        caution: estimate.is_locked
          ? LOCKED_STATUS_WRITE_CAUTION
          : estimate.status === "won"
            ? "This estimate is already recorded as won. Recording a " +
              "different outcome takes it out of the won figures."
            : null,
      };

  // 4. Park / chase. The same write as recording an outcome, gated the same
  //    way — plus one rule of its own: parking is for a conversation still in
  //    progress, so an estimate that already has an outcome cannot be parked.
  //    Won: the conversation ended in an order — if that is wrong, the fix is
  //    a corrected outcome, not a parked win. Lost: also blocked, and it is a
  //    choice worth stating — parking a lost estimate would silently reopen it
  //    in reporting (the CUSTOM StatusRef stops matching the lost list), which
  //    is a resurrection nobody said out loud. A conversation that restarts
  //    after a recorded loss is a new quote, or a correction (Record
  //    outcome) if the loss was keyed in error.
  const parkChase: EstimateControlState = missingSourceId
    ? {
        available: false,
        blockedReason: MISSING_SOURCE_ID_REASON,
        caution: null,
      }
    : estimate.status === "won"
      ? {
          available: false,
          blockedReason:
            "This estimate is already recorded as won. Parking is for a " +
            "conversation still in progress — this one ended in an order. " +
            "If that is wrong, use Record outcome to correct it.",
          caution: null,
        }
      : estimate.status === "lost"
        ? {
            available: false,
            blockedReason:
              "This estimate is already recorded as lost. Parking it would " +
              "quietly reopen it in reporting. If the customer has come " +
              "back, that is a new quote; if the loss was recorded in " +
              "error, use Record outcome to correct it.",
            caution: null,
          }
        : {
            available: true,
            blockedReason: null,
            caution: estimate.is_locked ? LOCKED_STATUS_WRITE_CAUTION : null,
          };

  return {
    recordOutcome,
    parkChase,
  };
}

// ── Intents (what would be posted, once a backend exists) ───────────────────

/**
 * The write this control would perform.
 *
 * Named as a verb because every write in this codebase is
 * `POST {resource}/{id}/actions/{verb}` — there are no PATCH or DELETE routes.
 * Deliberately carries the owning module's route rather than a view-module one:
 * `/views/...` is read-only, so a control on a composed page dispatches to the
 * module that owns the record.
 *
 * Nothing here executes. It exists so the mock journey can show exactly what
 * would be sent, which is the part worth agreeing before any of it is built.
 */
export interface EstimateWriteIntent {
  verb: string;
  /** Suggested owning-module route. Not yet built — see the module README. */
  route: string;
  payload: Record<string, unknown>;
  /** One line describing the effect, for the confirm step. */
  effect: string;
}

/**
 * The write that records an outcome.
 *
 * Maps onto the action that already exists —
 * `tharstern_estimating_module/actions/set_estimate_status.py` — which takes an
 * `EstimateStatus` plus a `custom_status_description` that Tharstern stores as
 * `CustomStatusDescription` and the replica reads back as `StatusRef`. The
 * description is mandatory for statuses 4 and 5 and meaningless for 1, so it is
 * only sent where it belongs.
 *
 * Park / chase confirms through this same builder: a park is the same
 * setstatus write carrying a CUSTOM code and its StatusRef text, so a second
 * intent function would be a duplicate, not a distinction.
 */
/**
 * How to refer to an estimate in a sentence a CSR reads.
 *
 * Tharstern's display number when it has replicated; the internal id otherwise,
 * which is at least addressable in support. Never "the estimate" — these
 * sentences stack in the session log, where that identifies nothing.
 */
function estimateLabel(estimate: EstimateDetailViewItem): string {
  return estimate.estimate_id || estimate.id;
}

/**
 * The real POST body for the setstatus write — `SetEstimateStatusBody` in
 * `modules/tharstern_estimating_module/api/schemas/estimating.py`. There is
 * no `internal_note` field: the body carries only `status`,
 * `custom_status_description`, `revision_estimate_id` and `expired`. A free
 * note has no home on a Tharstern estimate, so Record outcome and Park /
 * chase carry no note of their own rather than collecting text with nowhere
 * real to put it.
 */
export function recordOutcomeStatusBody(
  option: EstimateOutcomeOption,
  replacementEstimateId?: number,
): SetEstimateStatusBody {
  const isCustomStatus =
    option.tharsternStatus === CUSTOM_ALLOW_JOB ||
    option.tharsternStatus === CUSTOM_DISALLOW_JOB;
  return {
    status: option.tharsternStatus,
    ...(isCustomStatus ? { custom_status_description: option.statusRef } : {}),
    // Only ever sent for status 2, where the backend requires it. Sending it
    // elsewhere would put a link on an estimate nobody asked to link.
    ...(outcomeNeedsReplacementEstimate(option) && replacementEstimateId != null
      ? { revision_estimate_id: replacementEstimateId }
      : {}),
  };
}

export function recordOutcomeIntent(
  estimate: EstimateDetailViewItem,
  sourceEstimateId: number,
  option: EstimateOutcomeOption,
  replacementEstimateId?: number,
): EstimateWriteIntent {
  const resulting = outcomeResultingStatus(option);
  return {
    verb: "record_estimate_outcome",
    // The real route: POST /tharstern/estimating/estimates/{id}/setstatus
    // (tharstern_estimating_module/api/routes/estimating.py). Not the repo's
    // usual /actions/{verb} shape — this one mirrors Tharstern's own endpoint
    // name and predates that convention.
    //
    // `{id}` is Tharstern's own integer estimate id —
    // `information.source_estimate_id` on the detail response. Never
    // `estimate.id` (a dbt md5 surrogate) and never
    // `estimate.estimate_id` (a display string with no typed write contract,
    // even though — verified against the SQL, not assumed — it holds this
    // same integer, stringified, on every real row).
    route: `/tharstern/estimating/estimates/${sourceEstimateId}/setstatus`,
    payload: recordOutcomeStatusBody(option, replacementEstimateId),
    effect:
      // Names the estimate rather than saying "the estimate": this sentence is
      // replayed in the session log beneath the controls, where several actions
      // stack up and "the estimate" stops identifying anything.
      (option.classifiesAs === "still_open"
        ? `Records "${option.statusRef}" against ${estimateLabel(estimate)}. ` +
          "It stays open — reporting will not treat this as an outcome."
        : `Records "${option.statusRef}" against ${estimateLabel(estimate)}. ` +
          `It reports as ${resulting}.`) +
      (outcomeNeedsReplacementEstimate(option) && replacementEstimateId != null
        ? ` Tharstern links it to estimate ${replacementEstimateId} as the replacement.`
        : "") +
      (outcomeAllowsJobCreation(option)
        ? " A job can still be raised from it in Tharstern."
        : " Tharstern will no longer allow a job to be raised from it."),
  };
}
